import {accountsService, aoriService, channelsService, projectsService} from 'services';
import moment from 'moment-wrapper';
import BigNumber from 'bignumber.js';

const DEFAULT_VAT_DIVIDER = 1;

const accountsStore = {
  namespaced: true,
  state: {
    accountPath: [],
    accountProjectsSimple: [],
    accountChannels: [],
    aoriUserBalance: null,

    currentAccount: null,

    clearAccountsCacheFlag: true
  },
  getters: {
    rootAccount (state, getters) {
      return state.accountPath[0] || getters.currentAccount;
    },
    accountPath: state => state.accountPath,

    currentAccount: state => state.currentAccount,
    currentAccountId: (state, getters) => getters.currentAccount ? getters.currentAccount.id : null,

    accountChannels: state => state.accountChannels.map(c => c.id),
    accountProjectsSimple: state => state.accountProjectsSimple,

    currentAccountCurrencyId (state, getters) {
      const acc = getters.currentAccount;
      if (!acc) {
        return;
      }
      return acc.currency_id;
    },

    hasComponent (state, getters) {
      return (component) => {
        const account = getters.currentAccount;
        if (!account) {
          return false;
        }
        const components = new Set(account.components || []);
        if (Array.isArray(component)) {
          return component.every(c => components.has(c));
        }
        return components.has(component);
      };
    },

    // Aori
    aoriUserBalance (state) {
      return state.aoriUserBalance;
    },

    // account vat
    getVatDividerForDate: (state, getters, rootState, rootGetters) => (date) => {
      if (!rootGetters['common/vatOn']) {
        return false;
      }
      const currentAccount = getters.currentAccount;
      if (!currentAccount) {
        console.warn('[VUEX] No account for VAT, using default value {0}'.format(DEFAULT_VAT_DIVIDER));
        return DEFAULT_VAT_DIVIDER;
      }
      const vatRates = currentAccount.vats || [];
      date = moment(date || undefined);
      for (let i = vatRates.length - 1; i >= 0; i = i - 1) {
        const vat = vatRates[i];
        if (moment(vat.date) <= date) {
          return new BigNumber(vat.value);
        }
      }
      console.warn('[VAT] Rate not found for {0}. Using {1} as VAT'.format(
        date.format('YYYY-MM-DD'),
        DEFAULT_VAT_DIVIDER
      ));
      return new BigNumber(DEFAULT_VAT_DIVIDER);
    },
    vatDivider (state, getters) {
      return getters.getVatDividerForDate();
    },

    clearAccountsCacheFlag: (state) => state.clearAccountsCacheFlag
  },
  mutations: {
    SET_CURRENT_ACCOUNT (state, account) {
      state.currentAccount = account;
    },
    SET_ACCOUNT_PATH (state, {path}) {
      state.accountPath = path || [];
    },
    SET_ACCOUNT_CHANNELS (state, channels) {
      state.accountChannels = channels;
    },
    SET_ACCOUNT_SIMPLE_PROJECTS (state, projects) {
      state.accountProjectsSimple = projects;
    },

    // Aori
    SET_AORI_USER_BALANCE (state, balance) {
      state.aoriUserBalance = balance;
    },

    SET_ACCOUNTS_CACHE_CLEAR_FLAG (state, cacheValue) {
      state.clearAccountsCacheFlag = cacheValue;
    }
  },
  actions: {
    async SET_CURRENT_ACCOUNT_ID (context, accountId) {

      // do not fetch if account is the same
      if (context.getters.currentAccountId === accountId && !context.getters.clearAccountsCacheFlag) {
        return;
      }
      if (accountId) {
        const account = await accountsService.get(accountId);
        await context.dispatch('SET_CURRENT_ACCOUNT', account);
        context.commit('SET_ACCOUNTS_CACHE_CLEAR_FLAG', false);
        return;
      }
      await context.dispatch('SET_CURRENT_ACCOUNT', null);
    },
    async SET_CURRENT_ACCOUNT (context, account) {
      if (account) {
        const accountsPath = await accountsService.accountPath(account.id);
        context.commit('SET_ACCOUNT_PATH', {path: accountsPath.items});
      } else {
        context.commit('SET_ACCOUNT_PATH', {path: []});
      }
      context.commit('SET_CURRENT_ACCOUNT', account);
    },

    async DELETE_ACCOUNT (context, payload) {
      context.dispatch('sidebar/DELETE_ACCOUNT', payload, {root: true});
    },

    async FETCH_ACCOUNT_SIMPLE_PROJECTS (context) {
      const projectsSimple = (await projectsService.getAccountProjectsListSimple(
        context.getters.currentAccountId)
      ).items;

      context.commit('SET_ACCOUNT_SIMPLE_PROJECTS', projectsSimple);
    },

    async FETCH_ACCOUNT_CHANNELS (context) {
      const channels = await channelsService.getAccountChannels(context.getters.currentAccountId);

      context.commit('SET_ACCOUNT_CHANNELS', channels);
    },

    CLEAR_ACCOUNT_CACHE (context) {
      context.commit('SET_ACCOUNTS_CACHE_CLEAR_FLAG', true);
    },

    // Aori
    async FETCH_AORI_USER_BALANCE (context, accountId) {
      try {
        const account = context.rootGetters['sidebar/getAccountById'](accountId);
        if (account && account.options.email && context.rootGetters['common/isAori'] &&
          context.rootGetters['user/canUser']('READ_AORI_USER_BALANCE')) {
          const balance = await aoriService.getUserBalance(accountId);
          context.commit('SET_AORI_USER_BALANCE', balance);
        } else {
          context.commit('SET_AORI_USER_BALANCE', null);
        }
      } catch (error) {
        if (error.status === 400) {
          context.commit('SET_AORI_USER_BALANCE', null);
        }
      }
    }
  }
};

export default accountsStore;
